import {
  UseMutationOptions,
  useMutation as useMutationQuery,
  useQuery,
} from "@tanstack/react-query";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../utils/axiosInstance";

interface PropsState {
  route: string;
  requestBody?: any;
  key: any[];
  retry?: number | boolean;
  success?: any;
  onError?: any;
  keepPreviousData?: boolean;
  staleTime?: any;
  enabled?: boolean;
  refetchOnWindowFocus?: boolean;
  cacheTime?: any;
  networkMode?: any;
  headers?: any;
}

export const useFetchQuery = <T = any>(props: PropsState) => {
  const {
    requestBody,
    route,
    key,
    retry,
    success,
    onError,
    keepPreviousData = false,
    enabled,
    staleTime,
    cacheTime,
    networkMode,
    headers,
  } = props;

  const navigate = useNavigate();

  return useQuery<T>({
    queryKey: key,
    retry: retry || false,
    queryFn: ({ signal }) =>
      axiosInstance.get(route, {
        params: requestBody,
        signal,
        headers: headers,
      }),
    enabled: enabled,
    staleTime: staleTime || 0,
    cacheTime: cacheTime || 0,
    keepPreviousData: keepPreviousData || false,
    networkMode: networkMode,
    onSuccess: (data) => success && success(data),
    onError: (error: any) => {
      if (error?.response?.status === 444) {
        navigate("/app/access-denied", { replace: true });
      }
      if (onError) {
        onError(error);
      }
    },
  });
};

interface MutationProps<
  TData = unknown,
  TVariables = unknown,
  TError = unknown,
> extends UseMutationOptions<TData, TError, TVariables> {
  mutationFn: (variables?: TVariables) => Promise<TData>;
}

export const useMutation = <
  TData = unknown,
  TVariables = unknown,
  TError = unknown,
>(
  props: MutationProps<TData, TVariables, TError>,
) => {
  const { onSuccess, onError } = props;

  return useMutationQuery<TData, TError, TVariables>({
    ...props,
    onSuccess: (response: any, a, t) => {
      if (onSuccess) {
        onSuccess(response, a, t);
      }
      toast.success(
        response?.data?.message || "Action Performed Successfully.",
      );
    },
    onError: (err: any, a, t) => {
      if (err.response && err.response.data instanceof ArrayBuffer) {
        const decoder = new TextDecoder("utf-8");
        const errorData = decoder.decode(err.response.data);
        const errorJson = JSON.parse(errorData);

        toast.error(
          errorJson?.message || "Something went wrong, please try again.",
        );
      } else {
        if (onError) {
          onError(err, a, t);
        }
        toast.error(
          err?.response?.data?.message ||
            "Something went wrong, please try again.",
        );
      }
    },
  });
};
